import type { ReviewQueueItem } from '@eriko/shared-types';
import { ChevronDown, ChevronRight, Inbox } from 'lucide-react';
import { useState } from 'react';
import { useReconciliationStore } from '../../stores/reconciliation';
import { QueueItemRow } from './QueueItemRow';

interface Props {
  title: string;
  items: ReviewQueueItem[];
  defaultCollapsed?: boolean;
}

export function QueueGroup({ title, items, defaultCollapsed }: Props) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed ?? false);
  const selectAll = useReconciliationStore((s) => s.selectAll);
  const deselectAll = useReconciliationStore((s) => s.deselectAll);
  const selectedIds = useReconciliationStore((s) => s.selectedMatchIds);

  // Only matchId-bearing items can be selected (UNMATCHED txns have no
  // match yet). Already-CONFIRMED matches are also non-selectable —
  // re-confirming them would clobber their original audit trail.
  const selectableMatchIds = items
    .filter((i) => i.matchStatus !== 'CONFIRMED')
    .map((i) => i.matchId)
    .filter((id): id is string => !!id);

  const allSelected =
    selectableMatchIds.length > 0 &&
    selectableMatchIds.every((id) => selectedIds.has(id));

  return (
    <section className="rounded-md border border-surface-200 bg-surface-0 overflow-hidden">
      <header className="rounded-t-md flex items-center justify-between px-4 py-2 border-b border-surface-200 bg-surface-50">
        <button
          type="button"
          onClick={() => setCollapsed(!collapsed)}
          className="flex items-center gap-2 text-sm font-medium text-surface-900"
        >
          {collapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
          {title} <span className="text-surface-500">({items.length})</span>
        </button>
        {selectableMatchIds.length > 0 && (
          <label className="flex items-center gap-2 text-xs text-surface-600">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={() => {
                if (allSelected) deselectAll(selectableMatchIds);
                else selectAll(selectableMatchIds);
              }}
              className="h-4 w-4 rounded border-surface-300 text-surface-900 focus:ring-surface-500"
            />
            Select all
          </label>
        )}
      </header>
      {!collapsed &&
        (items.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 px-4 py-10 text-center">
            <Inbox className="h-6 w-6 text-surface-400" aria-hidden />
            <p className="text-sm font-medium text-surface-700">
              Nothing in {title.toLowerCase()}
            </p>
            <p className="text-xs text-surface-500">
              New items will appear here after the engine runs.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-surface-200">
            {items.map((item, i) => (
              <QueueItemRow
                key={item.matchId ?? `${item.transactionId}-${i}`}
                item={item}
              />
            ))}
          </ul>
        ))}
    </section>
  );
}
